import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/common/Button/Button';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import FilterChipsBar from '../../components/common/FilterChipsBar/FilterChipsBar';
import type { FilterChipEntry } from '../../components/common/FilterChipsBar/FilterChipsBar';
import FiltersGroup from '../../components/common/FiltersGroup/FiltersGroup';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import PageContainer, { PageBody } from '../../components/common/PageContainer/PageContainer';
import PageSizeSelect from '../../components/common/PageSizeSelect/PageSizeSelect';
import Pagination from '../../components/common/Pagination/Pagination';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import ToolbarRow from '../../components/common/ToolbarRow/ToolbarRow';
import { usePageSize } from '../../hooks/usePageSize';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import useTrainings from '../../hooks/useTrainings';
import { TrainingService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import type { TrainingItem } from '../../types/training';
import { canManageTrainings } from '../../utils/permissions';
import TrainingList from './components/TrainingList';
import styles from './Training.module.css';

const TRAININGS_QUERY_SCHEMA = {
  page: queryFilter.integer(1, { min: 1 }),
  search: queryFilter.string(),
  groups: queryFilter.array(),
};

const Training: React.FC = () => {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const canManage = canManageTrainings(roles);
  const [trainingToInactivate, setTrainingToInactivate] = useState<TrainingItem | null>(null);
  const [isInactivating, setIsInactivating] = useState(false);
  const { filters, setFilter, setFilters } = useQueryFilters(
    TRAININGS_QUERY_SCHEMA,
    { pageKey: 'page' },
  );
  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('trainings_page_size');
  const { search: searchTerm, groups: selectedGroups } = filters;

  const {
    trainings,
    pagination,
    loading,
    error,
    totalTrainings,
    groupOptions,
    refreshTrainings,
  } = useTrainings(page, pageSize, { searchTerm, groups: selectedGroups });

  const hasActiveFilters = searchTerm.trim().length > 0 || selectedGroups.length > 0;

  const handleClearFilters = useCallback(() => {
    setFilters({ search: '', groups: [] });
  }, [setFilters]);

  const handleRemoveGroupChip = useCallback((groupValue: string) => {
    setFilter('groups', (currentValues) => currentValues.filter((value) => value !== groupValue));
  }, [setFilter]);

  const handleCreateTraining = useCallback(() => {
    navigate('/treinos/novo');
  }, [navigate]);

  const handleApplyTraining = useCallback((training: TrainingItem) => {
    window.alert(`Aplicação do treino "${training.name}" em implementação.`);
  }, []);

  const handleEditTraining = useCallback((training: TrainingItem) => {
    navigate(`/treinos/${training.id}/editar`);
  }, [navigate]);

  const handleOpenTraining = useCallback((training: TrainingItem) => {
    navigate(`/treinos/${training.id}/editar`);
  }, [navigate]);

  const handleDuplicateTraining = useCallback((training: TrainingItem) => {
    window.alert(`Duplicação de "${training.name}" em implementação.`);
  }, []);

  const handleArchiveTraining = useCallback((training: TrainingItem) => {
    setTrainingToInactivate(training);
  }, []);

  const handleCancelInactivation = useCallback(() => {
    if (isInactivating) {
      return;
    }

    setTrainingToInactivate(null);
  }, [isInactivating]);

  const handleConfirmInactivation = useCallback(async () => {
    if (!trainingToInactivate) {
      return;
    }

    setIsInactivating(true);

    try {
      await TrainingService.inactivateTraining(trainingToInactivate.id);
      await refreshTrainings();
      setTrainingToInactivate(null);
    } finally {
      setIsInactivating(false);
    }
  }, [refreshTrainings, trainingToInactivate]);

  const buildMenuItems = useCallback((training: TrainingItem) => (canManage ? [
    { label: 'Editar', onClick: () => handleEditTraining(training) },
    { label: 'Duplicar', onClick: () => handleDuplicateTraining(training) },
    { label: 'Inativar', onClick: () => handleArchiveTraining(training), variant: 'danger' as const },
  ] : []), [canManage, handleArchiveTraining, handleDuplicateTraining, handleEditTraining]);

  const filterChips: FilterChipEntry[] = useMemo(() => selectedGroups.map((groupValue) => {
    const groupOption = groupOptions.find((option) => option.value === groupValue);

    return {
      filterKey: 'groups',
      value: groupValue,
      label: groupOption?.label ?? groupValue,
      onRemove: handleRemoveGroupChip,
    };
  }), [groupOptions, handleRemoveGroupChip, selectedGroups]);

  return (
    <PageContainer>
      <ToolbarRow className={styles['toolbar-row']}>
        {canManage && (
          <Button
            variant="primary"
            size="small"
            className={styles['create-button']}
            onClick={handleCreateTraining}
          >
            Novo treino
          </Button>
        )}

        <RecordCount label="Treinos cadastrados:" count={totalTrainings} />

        <FiltersGroup className={styles['filters-group']}>
          <SearchFilterBar
            searchValue={searchTerm}
            onSearchChange={(value) => setFilter('search', value)}
            searchPlaceholder="Pesquisar"
            loading={loading}
          />

          <MultiSelectFilter
            values={selectedGroups}
            onChange={(values) => setFilter('groups', values)}
            options={groupOptions}
            placeholder="Grupo"
            clearButtonLabel="Limpar filtro"
            hideChips
            showSelectionCount={false}
            className={styles['group-filter']}
          />

          <Button
            variant="ghost"
            size="small"
            icon="filter_alt"
            className={styles['filter-button']}
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
            aria-label="Limpar filtros"
            title={hasActiveFilters ? 'Limpar filtros' : 'Nenhum filtro ativo'}
          />
        </FiltersGroup>
      </ToolbarRow>

      <div className={styles['chips-row']}>
        <FilterChipsBar chips={filterChips} />
      </div>

      <PageBody>
        <TrainingList
          trainings={trainings}
          loading={loading}
          error={error}
          emptyMessage="Nenhum treino encontrado com os filtros selecionados."
          highlightTerm={searchTerm}
          onApply={handleApplyTraining}
          onOpen={handleOpenTraining}
          buildMenuItems={buildMenuItems}
          showApplyAction={canManage}
        />
      </PageBody>

      {trainings.length > 0 && (
        <div className={styles['bottom-row']}>
          <PageSizeSelect
            value={pageSize}
            options={pageSizeOptions}
            onChange={handlePageSizeChange}
          />
          <div className={styles['pagination-center']}>
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      <ConfirmModal
        open={canManage && Boolean(trainingToInactivate)}
        title="Inativar treino"
        message={trainingToInactivate ? `O treino "${trainingToInactivate.name}" deixará de aparecer na listagem ativa.` : undefined}
        confirmLabel="Inativar"
        cancelLabel="Cancelar"
        variant="danger"
        loading={isInactivating}
        onConfirm={handleConfirmInactivation}
        onCancel={handleCancelInactivation}
      />
    </PageContainer>
  );
};

export default Training;
